import React from 'react';

type MarkdownProps = {
  markdown: string;
};

function renderInline(markdownLine: string) {
  // Small inline Markdown subset: `code`, **bold**, *italic*, [text](href)
  // Note: emphasis parsing is intentionally simple (non-nested).
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let k = 0;

  const pushTextWithEmphasis = (text: string) => {
    if (!text) return;

    // bold
    if (text.includes('**')) {
      const chunks = text.split('**');
      chunks.forEach((chunk, idx) => {
        if (!chunk) return;
        if (idx % 2 === 1) {
          nodes.push(
            <strong key={`b-${k++}`} className="font-semibold text-slate-900 dark:text-white">
              {chunk}
            </strong>
          );
        } else {
          pushTextWithEmphasis(chunk);
        }
      });
      return;
    }

    // italic (single * ... *), best-effort
    if (text.includes('*')) {
      const chunks = text.split('*');
      chunks.forEach((chunk, idx) => {
        if (!chunk) return;
        if (idx % 2 === 1) {
          nodes.push(<em key={`i-${k++}`}>{chunk}</em>);
        } else {
          nodes.push(<React.Fragment key={`t-${k++}`}>{chunk}</React.Fragment>);
        }
      });
      return;
    }

    nodes.push(<React.Fragment key={`t-${k++}`}>{text}</React.Fragment>);
  };

  while (i < markdownLine.length) {
    const codeIdx = markdownLine.indexOf('`', i);
    const linkMatch = markdownLine.slice(i).match(/\[([^\]]+)\]\(([^)]+)\)/);
    const linkIdx = linkMatch?.index !== undefined ? i + linkMatch.index : -1;

    let next = -1;
    let kind: 'code' | 'link' | null = null;

    if (codeIdx !== -1) {
      next = codeIdx;
      kind = 'code';
    }
    if (linkIdx !== -1 && (next === -1 || linkIdx < next)) {
      next = linkIdx;
      kind = 'link';
    }

    if (next === -1 || kind === null) {
      pushTextWithEmphasis(markdownLine.slice(i));
      break;
    }

    if (next > i) {
      pushTextWithEmphasis(markdownLine.slice(i, next));
    }

    if (kind === 'code') {
      const end = markdownLine.indexOf('`', next + 1);
      if (end === -1) {
        pushTextWithEmphasis(markdownLine.slice(next));
        break;
      }
      const code = markdownLine.slice(next + 1, end);
      nodes.push(
        <code
          key={`c-${k++}`}
          className="px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-dark-800 text-sm"
        >
          {code}
        </code>
      );
      i = end + 1;
      continue;
    }

    // link
    if (linkMatch) {
      const label = linkMatch[1];
      const href = linkMatch[2];
      const isExternal = /^https?:\/\//i.test(href);
      nodes.push(
        <a
          key={`a-${k++}`}
          href={href}
          className="text-primary-600 dark:text-primary-400 hover:underline"
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {label}
        </a>
      );
      i = next + linkMatch[0].length;
      continue;
    }
  }

  return <>{nodes}</>;
}

function isOrderedListItem(line: string) {
  return /^\d+\.\s+/.test(line);
}

function stripOrderedPrefix(line: string) {
  return line.replace(/^\d+\.\s+/, '');
}

function listItemBody(line: string) {
  return line.replace(/^(-|\*)\s+/, '');
}

function isUnorderedListItem(line: string) {
  return /^(-|\*)\s+/.test(line);
}

function isTaskListItem(line: string) {
  return /^(-|\*)\s+\[( |x|X)\]\s+/.test(line);
}

function taskMeta(line: string) {
  const m = line.match(/^(-|\*)\s+\[( |x|X)\]\s+(.*)$/);
  if (!m) return null;
  return { checked: m[2].toLowerCase() === 'x', rest: m[3] };
}

export const CimmplePayMarkdown: React.FC<MarkdownProps> = ({ markdown }) => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');

  const blocks: React.ReactNode[] = [];
  let i = 0;
  let b = 0;

  const pushBlock = (node: React.ReactNode) => {
    blocks.push(<React.Fragment key={`blk-${b++}`}>{node}</React.Fragment>);
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    // fenced code ```
    if (line.startsWith('```')) {
      i++;
      const body: string[] = [];
      while (i < lines.length && !lines[i].startsWith('```')) {
        body.push(lines[i]);
        i++;
      }
      // consume closing fence
      if (i < lines.length && lines[i].startsWith('```')) i++;

      pushBlock(
        <pre className="not-prose my-6 overflow-x-auto rounded-xl border border-slate-200 dark:border-dark-800 bg-slate-950 p-4 text-sm text-slate-100">
          <code className="font-mono whitespace-pre">
            {body.join('\n')}
          </code>
        </pre>
      );
      continue;
    }

    // heading
    if (line.startsWith('#')) {
      const level = Math.min(6, line.match(/^#+/)?.[0].length ?? 1);
      const text = line.replace(/^#+\s+/, '');
      const cls =
        level === 1
          ? 'mt-10 text-3xl font-display font-bold text-slate-900 dark:text-white'
          : level === 2
            ? 'mt-10 text-2xl font-display font-bold text-slate-900 dark:text-white'
            : 'mt-8 text-xl font-semibold text-slate-900 dark:text-white';

      const heading =
        level === 1 ? (
          <h1 className={cls}>{renderInline(text)}</h1>
        ) : level === 2 ? (
          <h2 className={cls}>{renderInline(text)}</h2>
        ) : level === 3 ? (
          <h3 className={cls}>{renderInline(text)}</h3>
        ) : level === 4 ? (
          <h4 className={cls}>{renderInline(text)}</h4>
        ) : level === 5 ? (
          <h5 className={cls}>{renderInline(text)}</h5>
        ) : (
          <h6 className={cls}>{renderInline(text)}</h6>
        );

      pushBlock(heading);
      i++;
      continue;
    }

    // hr
    if (line.trim() === '---') {
      pushBlock(<hr className="my-10 border-slate-200 dark:border-dark-800" />);
      i++;
      continue;
    }

    // blockquote
    if (line.startsWith('>')) {
      const qs: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        qs.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      pushBlock(
        <blockquote className="my-6 border-l-4 border-primary-500/70 pl-4 text-slate-700 dark:text-dark-300">
          {qs.map((q, idx) => (
            <p key={idx} className="mb-3 last:mb-0">
              {renderInline(q)}
            </p>
          ))}
        </blockquote>
      );
      continue;
    }

    // lists
    if (isTaskListItem(line) || isUnorderedListItem(line) || isOrderedListItem(line)) {
      const isOrdered = isOrderedListItem(line) && !isUnorderedListItem(line);
      const ListTag = isOrdered ? 'ol' : 'ul';
      const items: React.ReactNode[] = [];

      while (i < lines.length) {
        const l = lines[i];
        if (l.trim() === '') break;

        if (isOrdered) {
          if (!isOrderedListItem(l)) break;
          const itemText = stripOrderedPrefix(l);
          items.push(
            <li key={items.length} className="my-2">
              {renderInline(itemText)}
            </li>
          );
          i++;
          continue;
        }

        // unordered / tasks
        if (!isUnorderedListItem(l)) break;

        const task = taskMeta(l);
        if (task) {
          items.push(
            <li key={items.length} className="my-2 flex gap-3">
              <input
                type="checkbox"
                checked={task.checked}
                readOnly
                className="mt-1 h-4 w-4 rounded border-slate-300 dark:border-dark-700"
                aria-label={task.checked ? 'Completed checklist item' : 'Incomplete checklist item'}
              />
              <span className="flex-1">{renderInline(task.rest)}</span>
            </li>
          );
        } else {
          items.push(
            <li key={items.length} className="my-2">
              {renderInline(listItemBody(l))}
            </li>
          );
        }
        i++;
      }

      pushBlock(
        <ListTag
          className={
            isOrdered
              ? 'my-6 list-decimal pl-6 text-slate-700 dark:text-dark-300'
              : 'my-6 list-disc pl-6 text-slate-700 dark:text-dark-300'
          }
        >
          {items}
        </ListTag>
      );
      continue;
    }

    // paragraph (consume until blank)
    const ps: string[] = [];
    while (i < lines.length && lines[i].trim() !== '') {
      // if we hit a structural line, break
      const l = lines[i];
      if (l.startsWith('#') || l.startsWith('```') || l.startsWith('>') || l.trim() === '---') break;
      if (isUnorderedListItem(l) || isOrderedListItem(l)) break;
      ps.push(l);
      i++;
    }

    pushBlock(
      <p className="my-4 text-slate-700 dark:text-dark-300 leading-relaxed">
        {ps.map((p, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 ? ' ' : null}
            {renderInline(p)}
          </React.Fragment>
        ))}
      </p>
    );
  }

  return <div className="max-w-none">{blocks}</div>;
};

export function splitLeadingH1(markdown: string): { title: string | null; body: string } {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const firstNonEmpty = lines.findIndex((l) => l.trim() !== '');
  if (firstNonEmpty === -1) return { title: null, body: markdown };
  const first = lines[firstNonEmpty];
  if (!first.startsWith('# ')) return { title: null, body: markdown };

  const title = first.slice(2).trim();
  const rest = [...lines.slice(0, firstNonEmpty), ...lines.slice(firstNonEmpty + 1)].join('\n').trimStart();
  return { title, body: rest };
}
